import type { FC, ReactNode } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import type { DialogProps } from "@mui/material/Dialog";
import { useMutation } from "@tanstack/react-query";
import { Stack, Box, Typography, Button, Dialog } from "@mui/material";
import * as FormDialog from "./FormDialog";

type ConfirmDeleteDialogProps = {
  isOpen: boolean;
  onDelete: () => Promise<unknown>;
  onClose: () => void;
  headerText: string;
  message: ReactNode;
  details?: ReactNode;
  confirmButtonLabel: string;
  errorMessage?: string;
  variant?: "standard" | "severe";
  confirmButtonColor?: "primary" | "error";
} & Partial<DialogProps>;
export const ConfirmDeleteDialog: FC<ConfirmDeleteDialogProps> = ({
  onClose,
  onDelete,
  isOpen,
  headerText,
  message,
  details,
  confirmButtonLabel,
  errorMessage,
  variant = "standard",
  confirmButtonColor = "primary",
  ...dialogProps
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    reset: resetForm,
  } = useForm<{ confirmation: string }>({
    defaultValues: { confirmation: "" },
  });
  const { isPending, mutate, isError, reset } = useMutation({ mutationFn: onDelete });

  const handleConfirmDelete = () => mutate(undefined, { onSuccess: handleClose });

  const handleClose = () => {
    reset();
    onClose();
    resetForm();
  };

  const confirmationWord = t("ConfirmDeleteDialog.Input.ConfirmationWord");

  return (
    <Dialog
      slotProps={{ paper: { sx: { maxWidth: variant === "severe" ? "37.5rem" : undefined } } }}
      open={isOpen}
      onClose={handleClose}
      {...dialogProps}
    >
      <FormDialog.Form onSubmit={handleSubmit(handleConfirmDelete)}>
        <FormDialog.Title>{headerText}</FormDialog.Title>
        <FormDialog.Content sx={details ? { scrollbarGutter: "stable" } : undefined}>
          <Stack>
            {(isError || Boolean(errorMessage)) && (
              <FormDialog.FormError isError={isError || Boolean(errorMessage)} errorMessage={errorMessage} />
            )}

            <Typography
              sx={{
                mb: 4,
                color: theme => theme.palette.text.secondary,
                fontSize: theme => theme.typography.pxToRem(14),
              }}
            >
              {message}
            </Typography>
            {details}
            {variant === "severe" && (
              <FormDialog.TextField
                control={control}
                name="confirmation"
                rules={{
                  validate: value =>
                    value === confirmationWord ||
                    t("ConfirmDeleteDialog.Input.ValidationMessage", { confirmationWord }),
                }}
                label={
                  <Box component="span" sx={{ fontWeight: "normal" }}>
                    <Translate
                      translationKey="ConfirmDeleteDialog.Input.Label"
                      values={{
                        confirmationWord: (
                          <Box component="span" sx={{ fontWeight: "bold" }}>
                            {confirmationWord}
                          </Box>
                        ),
                      }}
                    />
                  </Box>
                }
              />
            )}
          </Stack>
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <Button variant="outlined" onClick={handleClose}>
            {t("ConfirmDeleteDialog.CancelButton")}
          </Button>
          <Button type="submit" color={confirmButtonColor} loading={isPending}>
            {confirmButtonLabel}
          </Button>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </Dialog>
  );
};
